import React, { useRef } from 'react'
import { Box } from 'lucide-react'
import mapboxgl from 'mapbox-gl'

interface Toggle3DControlProps {
  map: mapboxgl.Map | null
  is3DEnabled: boolean
  onToggle3D: (enabled: boolean) => void
  className?: string
}

export function Toggle3DControl({ map, is3DEnabled, onToggle3D, className = '' }: Toggle3DControlProps) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  
  const handleToggle = () => {
    if (!map) return
    
    const newState = !is3DEnabled
    onToggle3D(newState)
    
    // Animate the pitch change
    if (newState) {
      // Enable 3D view with pitch
      map.easeTo({
        pitch: 50,
        duration: 500
      })
    } else {
      // Disable 3D view - flat
      map.easeTo({
        pitch: 0,
        duration: 500
      })
    }
  }

  const startTiltAdjustment = (delta: number) => {
    if (!map) return
    
    // Immediate adjustment
    adjustTilt(delta)
    
    // Continue adjusting while held
    intervalRef.current = setInterval(() => {
      adjustTilt(delta)
    }, 50) // Adjust every 50ms for smooth movement
  }
  
  const stopTiltAdjustment = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  const adjustTilt = (delta: number) => {
    if (!map) return
    const currentPitch = map.getPitch()
    const newPitch = Math.max(0, Math.min(85, currentPitch + delta))
    map.setPitch(newPitch) // Use setPitch for immediate updates
  }

  return (
    <div className="flex flex-col">
      <button
        onClick={handleToggle}
        className={`relative flex items-center justify-center ${className}`}
        style={{ 
          width: '30px', 
          height: '30px',
          backgroundColor: 'transparent',
          borderRadius: '4px'
        }}
        title={is3DEnabled ? "Disable 3D view" : "Enable 3D view"}
      >
        <div className="flex items-center justify-center">
          <span className={`text-xs font-medium transition-colors ${is3DEnabled ? 'text-emerald-400' : 'text-gray-400'} hover:text-emerald-300`}>
            3D
          </span>
        </div>
      </button>
      
      {/* Tilt Controls - shown when 3D is enabled */}
      {is3DEnabled && (
        <div className="flex flex-col gap-0.5 mt-1">
          <button
            onMouseDown={() => startTiltAdjustment(2)}
            onMouseUp={stopTiltAdjustment}
            onMouseLeave={stopTiltAdjustment}
            onTouchStart={() => startTiltAdjustment(2)}
            onTouchEnd={stopTiltAdjustment}
            className="flex items-center justify-center hover:bg-emerald-400/20 transition-colors rounded group"
            style={{ 
              width: '30px', 
              height: '20px',
              backgroundColor: 'transparent'
            }}
            title="Increase tilt"
          >
            <span className="text-emerald-400 group-hover:text-emerald-300 text-xs">▲</span>
          </button>
          <button
            onMouseDown={() => startTiltAdjustment(-2)}
            onMouseUp={stopTiltAdjustment}
            onMouseLeave={stopTiltAdjustment}
            onTouchStart={() => startTiltAdjustment(-2)}
            onTouchEnd={stopTiltAdjustment}
            className="flex items-center justify-center hover:bg-emerald-400/20 transition-colors rounded group"
            style={{ 
              width: '30px', 
              height: '20px',
              backgroundColor: 'transparent'
            }}
            title="Decrease tilt"
          >
            <span className="text-emerald-400 group-hover:text-emerald-300 text-xs">▼</span>
          </button>
        </div>
      )}
    </div>
  )
}